import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Shield, ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';
import { AdminUserModal } from '@/components/admin/users/AdminUserModal';
import { AdminUserRow } from '@/components/admin/users/AdminUserRow';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Modal states
  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | 'password'
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({ email: '', name: '', role: 'editor', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin-users/me`, {
          credentials: 'include'
        });
        
        if (!res.ok) {
          navigate('/admin/login');
          return;
        }
        
        const user = await res.json();
        setCurrentUser(user);
        
        if (user.role !== 'super_admin') {
          navigate('/blog-admin');
          return;
        }
        
        await fetchAdmins();
      } catch {
        navigate('/admin/login');
      }
    };
    
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin-users`, {
        credentials: 'include'
      });
      
      if (!res.ok) throw new Error('Kon admins niet ophalen');
      const data = await res.json();
      setAdmins(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (mode, admin = null) => {
    setModalMode(mode);
    setSelectedAdmin(admin);
    setFormError('');
    setShowPassword(false);
    
    if (mode === 'create') {
      setFormData({ email: '', name: '', role: 'editor', password: '' });
    } else if (mode === 'edit' && admin) {
      setFormData({ email: admin.email, name: admin.name, role: admin.role, password: '' });
    } else if (mode === 'password') {
      setFormData({ password: '' });
    }
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedAdmin(null);
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');

    try {
      let res;
      
      if (modalMode === 'create') {
        res = await fetch(`${API_URL}/api/admin-users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(formData)
        });
      } else if (modalMode === 'edit') {
        res = await fetch(`${API_URL}/api/admin-users/${selectedAdmin.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: formData.name, role: formData.role })
        });
      } else if (modalMode === 'password') {
        res = await fetch(`${API_URL}/api/admin-users/${selectedAdmin.id}/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ new_password: formData.password })
        });
      }

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Er ging iets mis');
      }

      closeModal();
      await fetchAdmins();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`Weet je zeker dat je ${admin.email} wilt verwijderen?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin-users/${admin.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Kon admin niet verwijderen');
      }
      
      await fetchAdmins();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleToggleActive = async (admin) => {
    try {
      const res = await fetch(`${API_URL}/api/admin-users/${admin.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !admin.is_active })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Kon status niet wijzigen');
      }
      
      await fetchAdmins();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#2563EB] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-12" data-testid="admin-users-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Users className="w-8 h-8 text-[#2563EB]" />
              Admin Gebruikers
            </h1>
            <p className="text-slate-600 mt-1">Beheer admin accounts en rollen</p>
          </div>
          
          <button
            onClick={() => openModal('create')}
            className="flex items-center gap-2 bg-[#2563EB] text-white px-4 py-2.5 rounded-lg hover:bg-[#1D4ED8] transition-colors font-medium"
            data-testid="create-admin-btn"
          >
            <Plus size={20} />
            Nieuwe Admin
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {/* Legacy Admin Notice */}
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg mb-6 flex items-start gap-3">
          <ShieldCheck size={20} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Legacy Super Admin</p>
            <p className="text-sm text-blue-600">
              De originele admin (admin@netblitz.eu) is altijd actief en kan hier niet worden beheerd.
            </p>
          </div>
        </div>

        {/* Admin List */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {admins.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Nog geen extra admin accounts.</p>
              <p className="text-sm">Klik op "Nieuwe Admin" om er een aan te maken.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Gebruiker</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Rol</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Status</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Laatste Login</th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {admins.map((admin) => (
                    <AdminUserRow
                      key={admin.id}
                      admin={admin}
                      onEdit={(a) => openModal('edit', a)}
                      onDelete={handleDelete}
                      onToggleActive={handleToggleActive}
                      onResetPassword={(a) => openModal('password', a)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Role Legend */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Rollen Uitleg</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <ShieldCheck size={18} className="text-purple-700" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Super Admin</p>
                <p className="text-sm text-slate-500">Volledige toegang inclusief gebruikersbeheer.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Shield size={18} className="text-blue-700" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Editor</p>
                <p className="text-sm text-slate-500">Kan content beheren maar geen gebruikersbeheer.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unified Modal */}
      <AdminUserModal
        isOpen={modalMode !== null}
        onClose={closeModal}
        title={
          modalMode === 'create' ? 'Nieuwe Admin' :
          modalMode === 'edit' ? 'Admin Bewerken' :
          modalMode === 'password' ? `Wachtwoord Resetten - ${selectedAdmin?.email}` : ''
        }
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        saving={saving}
        error={formError}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        mode={modalMode || 'create'}
      />
    </div>
  );
};

export default AdminUsersPage;
